import { useEffect, useState } from 'react';

/** Writes synchronously with the edit, not in an unmount/debounce callback. */
export function useTicketDraft<T extends object>(key: string, initial: T) {
  const [loaded] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return { value: null, failed: false };
      const parsed: unknown = JSON.parse(raw);
      if (
        !parsed ||
        typeof parsed !== 'object' ||
        Object.keys(initial).some(
          (field) => typeof (parsed as Record<string, unknown>)[field] !== 'string',
        )
      ) {
        return { value: null, failed: true };
      }
      return { value: parsed as T, failed: false };
    } catch {
      return { value: null, failed: true };
    }
  });
  const [values, update] = useState(initial);
  const [recoverable, setRecoverable] = useState<T | null>(loaded.value);
  const [failed, setFailed] = useState(loaded.failed);

  function setValues(next: T) {
    update(next);
    try {
      localStorage.setItem(key, JSON.stringify(next));
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }
  function discard() {
    try {
      localStorage.removeItem(key);
      setRecoverable(null);
      update(initial);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }
  function recover() {
    if (recoverable) {
      update(recoverable);
      setRecoverable(null);
    }
  }
  function confirm(submitted: T) {
    update((current) =>
      JSON.stringify(current) === JSON.stringify(submitted) ? initial : current,
    );
    // A late response must not erase a draft edited in another tab.
    try {
      if (localStorage.getItem(key) === JSON.stringify(submitted)) localStorage.removeItem(key);
    } catch {
      /* Keep the draft if storage cannot be accessed. */
    }
  }

  useEffect(() => {
    if (!failed) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    const link = (event: MouseEvent) => {
      if (
        (event.target as Element).closest('a[href]') &&
        !window.confirm('El borrador no se pudo guardar. ¿Salir y perder los cambios?')
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener('beforeunload', warn);
    document.addEventListener('click', link, true);
    return () => {
      window.removeEventListener('beforeunload', warn);
      document.removeEventListener('click', link, true);
    };
  }, [failed]);
  return { values, setValues, recoverable, recover, discard, confirm, failed };
}
